/* Enhance the review widget and preserve its native controls. */
(() => {
  if (window.masProductLowerReady) return;
  window.masProductLowerReady = true;
  const bound = new WeakSet();
  const setText = (node, text) => { if (node.textContent !== text) node.textContent = text; };

  function watch(widget, update, attributes) {
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    });
    function run() {
      queued = false;
      observer.disconnect();
      if (!widget.isConnected) return;
      update();
      observer.observe(widget, {childList:true, subtree:true, characterData:true, attributes:true, attributeFilter:attributes});
    }
    widget.addEventListener('change', run);
    run();
  }

  function enhanceReviews(widget) {
    let expanded = false;
    watch(widget, () => {
      const histogram = widget.querySelector('.jdgm-histogram');
      if (histogram) {
        let breakdown = widget.querySelector('.mas-rating-toggle');
        if (!breakdown) {
          breakdown = document.createElement('button');
          breakdown.type = 'button';
          breakdown.className = 'mas-rating-toggle';
          breakdown.textContent = 'Rating breakdown';
          breakdown.setAttribute('aria-label','Rating breakdown');
          breakdown.setAttribute('aria-expanded','false');
          histogram.before(breakdown);
          breakdown.addEventListener('click', () => {
            const open = breakdown.getAttribute('aria-expanded') !== 'true';
            breakdown.setAttribute('aria-expanded',String(open));
            widget.classList.toggle('mas-rating-open',open);
          });
        }
        if (!histogram.id) histogram.id = 'mas-rating-' + (widget.closest('.jdgm-review-widget')?.dataset.productId || 'product');
        breakdown.setAttribute('aria-controls',histogram.id);
      }
      const summary = widget.querySelector('.jdgm-rev-widg__summary-inner');
      const reviews = widget.querySelector('.jdgm-rev-widg__reviews');
      const average = Number(widget.dataset.averageRating);
      if (summary && Number.isFinite(average) && Number(widget.dataset.numberOfReviews) > 0) {
        let score = summary.querySelector('.mas-review-score');
        if (!score) {
          score = document.createElement('strong');
          score.className = 'mas-review-score';
          score.setAttribute('aria-hidden','true');
          summary.prepend(score);
        }
        setText(score, average.toFixed(2));
      }
      if (!reviews) return;
      if (!reviews.id) reviews.id = 'mas-review-list-' + (widget.closest('.jdgm-review-widget')?.dataset.productId || 'product');
      let toggle = widget.querySelector('.mas-review-toggle');
      if (!toggle) {
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mas-review-toggle';
        reviews.after(toggle);
        toggle.addEventListener('click', () => {
          expanded = !expanded;
          widget.classList.toggle('mas-reviews-collapsed', !expanded);
          toggle.setAttribute('aria-expanded', String(expanded));
          setText(toggle, expanded ? 'Show fewer reviews' : 'Show more reviews');
        });
      }
      const more = reviews.querySelectorAll(':scope > .jdgm-rev').length > 2;
      toggle.setAttribute('aria-controls',reviews.id);
      toggle.hidden = !more;
      toggle.setAttribute('aria-expanded', String(expanded));
      setText(toggle, expanded ? 'Show fewer reviews' : 'Show more reviews');
      widget.classList.toggle('mas-reviews-collapsed', more && !expanded);
      widget.classList.add('mas-reviews-ready');
    }, ['data-average-rating','data-number-of-reviews']);
    // A native filter/sort request should show its complete result page, not a second truncated list.
    widget.addEventListener('change', event => {
      if (event.target.matches('.jdgm-sort-dropdown')) expanded = true;
    }, true);
    widget.addEventListener('click', event => {
      if (event.target.closest('.jdgm-histogram__row, .jdgm-paginate__page')) {
        expanded = true;
        widget.classList.remove('mas-reviews-collapsed');
      }
    }, true);
  }

  function discover() {
    document.querySelectorAll('.mas-product-page .jdgm-rev-widg').forEach(widget => {
      if (bound.has(widget)) return;
      bound.add(widget);
      enhanceReviews(widget);
    });
  }
  function start() {
    discover();
    const root = document.getElementById('MainContent');
    if (!root) return;
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('.jdgm-rev-widg') || node.querySelector('.jdgm-rev-widg'))))) discover();
    }).observe(root, {childList:true, subtree:true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();

/* Record visits when a PDP opens, not only after someone scrolls to its footer. */
if (!customElements.get('mas-recently-viewed')) {
  customElements.define('mas-recently-viewed', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      const current = this.dataset.id;
      let previous = [];
      try {
        const stored = localStorage.getItem('theme4:recently:id') || '';
        previous = [...new Set(stored.split(',').filter(id => /^\d+$/.test(id) && id !== current))];
        if (/^\d+$/.test(current)) localStorage.setItem('theme4:recently:id', [current,...previous].slice(0,10).join(','));
      } catch (_) { /* Browsing history remains optional if storage is unavailable. */ }
      if (!previous.length || !current) return;
      this.historyIds = previous.slice(0, Number(this.dataset.limit) || 10);
      // Observe the section because the empty component deliberately has no layout box.
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadHistory();
      }, {rootMargin:'500px'});
      this.observer.observe(this.parentElement);
    }
    async loadHistory() {
      try {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('q', this.historyIds.map(id => 'id:' + id).join(' OR '));
        const response = await fetch(url, {signal:this.controller.signal});
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const section = doc.querySelector('mas-recently-viewed .mas-recent-history');
        if (!section || !this.isConnected) return;
        const track = section.querySelector('.mas-recent-history__track');
        const cards = new Map([...track.children].map(card => [card.dataset.historyId,card]));
        track.replaceChildren(...this.historyIds.map(id => cards.get(id)).filter(Boolean));
        if (!track.children.length) return;
        this.replaceChildren(section);
        this.hidden = false;
        const previous = this.querySelector('[data-history-prev]');
        const next = this.querySelector('[data-history-next]');
        const update = () => {
          this.querySelector('.mas-recent-history__nav').hidden = track.scrollWidth <= track.clientWidth + 2;
          previous.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        };
        const scroll = direction => track.scrollBy({left:direction * track.clientWidth * .85,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
        previous.addEventListener('click', () => scroll(-1), {signal:this.controller.signal});
        next.addEventListener('click', () => scroll(1), {signal:this.controller.signal});
        track.addEventListener('scroll', update, {passive:true,signal:this.controller.signal});
        this.resize = new ResizeObserver(update);
        this.resize.observe(track);
        update();
        document.dispatchEvent(new CustomEvent('currency:update'));
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.started = false;
    }
  });
}
